import React from 'react';
import { Brain, TrendingUp, BarChart3, Clock, Lightbulb, MessageSquare, Sparkles } from 'lucide-react';

const FEATURES = [
  {
    icon: Brain,
    title: 'Adaptive Intelligence',
    description: 'AI engine that learns your cognitive patterns and adjusts learning paths in real-time.',
  },
  {
    icon: TrendingUp,
    title: 'Reinforcement Learning Loop',
    description: 'Every attempt strengthens the model. Spaced repetition meets AI optimization.',
  },
  {
    icon: BarChart3,
    title: 'Smart Analytics',
    description: 'Deep insights into your performance. Identify weak areas before exams.',
  },
  {
    icon: Clock,
    title: 'Auto Scheduling',
    description: 'Optimal study schedules based on your learning pace and exam timeline.',
  },
  {
    icon: Lightbulb,
    title: 'Dynamic Question Generation',
    description: 'Infinite questions using RAG-powered AI - you never run out of relevant practice material.',
  },
  {
    icon: MessageSquare,
    title: 'AI Tutor (Lexa)',
    description: '24/7 AI assistant that explains concepts, debugs mistakes, and guides you.',
  },
];

function FeatureCard({ icon: Icon, title, description, isDarkTheme }) {
  const cardBg = isDarkTheme ? 'bg-white/5 border-white/10 hover:border-brand-cyan/40' : 'bg-white border-gray-200 hover:border-brand-blue/40';
  const textColor = isDarkTheme ? 'text-white' : 'text-navy';
  const textMuted = isDarkTheme ? 'text-white/70' : 'text-text-slate';

  return (
    <div className={`group rounded-card border ${cardBg} p-3 sm:p-8 transition-all duration-300 hover:shadow-brand-md`}>
      <div className="p-2 sm:p-4 rounded-btn bg-brand-gradient w-fit mb-2 sm:mb-6 transition-transform group-hover:scale-110">
        <Icon className="text-white" size={28} />
      </div>
      <h3 className={`text-sm sm:text-xl font-bold ${textColor} mb-1 sm:mb-3`}>{title}</h3>
      <p className={`text-xs sm:text-base ${textMuted} leading-relaxed`}>{description}</p>
    </div>
  );
}

export default function FeaturesSection({ isDarkTheme }) {
  const textColor = isDarkTheme ? 'text-white' : 'text-navy';
  const textMuted = isDarkTheme ? 'text-white/70' : 'text-text-slate';
  const badgeBg = isDarkTheme ? 'bg-white/10 border-white/20' : 'bg-surface-light border-gray-200';

  return (
    <section id="features" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
      <div className="text-center mb-8 sm:mb-16">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${badgeBg} mb-6`}>
          <Sparkles size={16} className="text-brand-blue" />
          <span className={`text-sm font-semibold ${isDarkTheme ? 'text-white' : 'text-brand-blue'}`}>Core Features</span>
        </div>
        <h2 className={`font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4 ${textColor}`}>
          Intelligent Features,
          <span className="block bg-brand-gradient bg-clip-text text-transparent">Real Results</span>
        </h2>
        <p className={`text-lg md:text-xl ${textMuted} max-w-2xl mx-auto`}>
          Everything you need to master competitive exams with AI-powered precision
        </p>
      </div>

      {/* 2 columns x 3 rows on mobile instead of a single stacked column
          (explicit user request, docs/DECISIONS.md, "Landing page mobile
          layout" decision) - unchanged at md:/lg: (2 cols tablet, 3 cols
          desktop). */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-8">
        {FEATURES.map((feature) => (
          <FeatureCard key={feature.title} isDarkTheme={isDarkTheme} {...feature} />
        ))}
      </div>
    </section>
  );
}
